'use client';

import React, { useState } from 'react';
import { resolveCustomThemeHref } from '../utils/linkHelpers';
import { getBaseImageUrl } from '@/constants/hooks';

interface OfferDetailModalProps {
    data: any;
    context?: {
        domain?: string;
        store_slug?: string;
        slug_type?: string;
        category_slug?: string;
        promotion_slug?: string;
        out_link?: string;
    };
    onClose: () => void;
}

const hasHtmlContent = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const fallbackCopy = (text: string, callback: () => void) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
        if (document.execCommand('copy')) callback();
    } catch {
        // Clipboard unavailable — the copy silently no-ops.
    }
    document.body.removeChild(textArea);
};

/**
 * The offer-details popup itself, extracted out of ProductDetailTrigger.tsx
 * so it can be shown two ways: a click on a card's "View Details" (the
 * trigger still lives in ProductDetailTrigger.tsx), or automatically from a
 * ?show=true&p_id=<unique_id> URL (OfferDetailModalFromQuery.tsx) — the
 * second tab OfferOutUrl.tsx / LinkComponent.tsx's trackOutUrl flow opens.
 */
const OfferDetailModal: React.FC<OfferDetailModalProps> = ({ data, context, onClose }) => {
    const [copied, setCopied] = useState(false);

    const rawImage = data?.merchant_logo || data?.product_image || data?.offer_image || data?.category_image || data?.image || data?.banner_image;
    const image = getBaseImageUrl(context?.domain || '', rawImage, '');
    const title = data?.offer_title || data?.name || '';
    const discount = data?.discount;
    const isLifeTime = Boolean(data?.life_time);
    const description = data?.offer_detail || data?.description || data?.product_description;
    const couponCode = data?.coupon_code;
    const dealHref = resolveCustomThemeHref('[url]', data, context) || '#';
    const ctaLabel = data?.btn_text || 'Get Deal Now';
    const merchantName = data?.merchant_name;
    const merchantHref = data?.merchant_url || '';

    const handleCopy = () => {
        if (!couponCode) return;
        const performCopy = () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(couponCode).then(performCopy).catch(() => fallbackCopy(couponCode, performCopy));
        } else {
            fallbackCopy(couponCode, performCopy);
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: 'relative',
                    backgroundColor: 'var(--theme-color-background, #ffffff)',
                    color: 'var(--theme-color-text, #374151)',
                    fontFamily: 'var(--theme-font-family, Arial, sans-serif)',
                    borderRadius: 'calc(12px * var(--theme-radius-scale, 1))',
                    width: '100%',
                    maxWidth: '420px',
                    maxHeight: '85vh',
                    overflowY: 'auto',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                }}
            >
                {discount && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '14px',
                            left: '14px',
                            backgroundColor: 'var(--theme-color-accent, #ff912f)',
                            color: '#ffffff',
                            fontSize: 'calc(11px * var(--theme-font-scale, 1))',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            padding: '5px 12px',
                            borderRadius: 'calc(999px * var(--theme-radius-scale, 1))',
                        }}
                    >
                        {discount}
                    </div>
                )}

                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    style={{
                        position: 'absolute',
                        top: '12px',
                        right: '14px',
                        border: 'none',
                        background: 'none',
                        fontSize: 'calc(20px * var(--theme-font-scale, 1))',
                        lineHeight: 1,
                        color: '#64748b',
                        cursor: 'pointer',
                    }}
                >
                    ×
                </button>

                <div style={{ padding: '54px 24px 24px', textAlign: 'center', fontSize: 'calc(16px * var(--theme-font-scale, 1))' }}>
                    {image && (
                        <div
                            style={{
                                border: '1px solid #e2e8f0',
                                borderRadius: 'calc(10px * var(--theme-radius-scale, 1))',
                                padding: '12px',
                                display: 'inline-flex',
                                marginBottom: '18px',
                            }}
                        >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={image} alt={title} style={{ maxWidth: '220px', maxHeight: '180px', objectFit: 'contain' }} />
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '14px' }}>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                backgroundColor: 'color-mix(in srgb, var(--theme-color-primary, #f23017) 12%, white)',
                                color: 'var(--theme-color-primary, #f23017)',
                                fontSize: 'calc(11px * var(--theme-font-scale, 1))',
                                fontWeight: 700,
                                padding: '4px 10px',
                                borderRadius: 'calc(999px * var(--theme-radius-scale, 1))',
                            }}
                        >
                            ● Verified
                        </span>
                        {isLifeTime && (
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    backgroundColor: 'color-mix(in srgb, var(--theme-color-accent, #ff912f) 16%, white)',
                                    color: 'var(--theme-color-accent, #ff912f)',
                                    fontSize: 'calc(11px * var(--theme-font-scale, 1))',
                                    fontWeight: 700,
                                    padding: '4px 10px',
                                    borderRadius: 'calc(999px * var(--theme-radius-scale, 1))',
                                }}
                            >
                                📅 Life Time
                            </span>
                        )}
                    </div>

                    {title && (
                        <h3 style={{ fontSize: 'calc(17px * var(--theme-font-scale, 1))', fontWeight: 700, color: 'var(--theme-color-secondary, #222e48)', lineHeight: 1.4, marginBottom: '18px' }}>
                            {title}
                        </h3>
                    )}

                    {couponCode ? (
                        <div style={{ marginBottom: '12px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    backgroundColor: '#f8fafc',
                                    border: '2px dashed #cbd5e1',
                                    borderRadius: '10px',
                                    padding: '6px',
                                    marginBottom: '8px',
                                }}
                            >
                                <span
                                    style={{
                                        fontFamily: 'monospace',
                                        fontWeight: 700,
                                        fontSize: 'calc(16px * var(--theme-font-scale, 1))',
                                        color: 'var(--theme-color-secondary, #222e48)',
                                        padding: '0 14px',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    {couponCode}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleCopy}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        border: 'none',
                                        borderRadius: 'calc(8px * var(--theme-radius-scale, 1))',
                                        padding: '10px 18px',
                                        fontSize: 'calc(13px * var(--theme-font-scale, 1))',
                                        fontWeight: 700,
                                        color: '#ffffff',
                                        backgroundColor: copied ? 'var(--theme-color-secondary, #222e48)' : 'var(--theme-color-primary, #f23017)',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {copied ? 'Copied' : 'Copy'}
                                </button>
                            </div>
                            <small style={{ display: 'block', fontSize: 'calc(10px * var(--theme-font-scale, 1))', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Use code at checkout
                            </small>
                        </div>
                    ) : (
                        <a
                            href={dealHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: 'var(--theme-color-primary, #f23017)',
                                color: '#ffffff',
                                fontWeight: 700,
                                fontSize: 'calc(14px * var(--theme-font-scale, 1))',
                                padding: '10px 28px',
                                borderRadius: 'calc(999px * var(--theme-radius-scale, 1))',
                                textDecoration: 'none',
                                marginBottom: '12px',
                            }}
                        >
                            {ctaLabel} →
                        </a>
                    )}

                    {description && (
                        <>
                            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '14px 0' }} />
                            <small style={{ display: 'block', fontSize: 'calc(11px * var(--theme-font-scale, 1))', fontWeight: 700, color: 'var(--theme-color-accent, #ff912f)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px', textAlign: 'left' }}>
                                Offer Details
                            </small>
                            {hasHtmlContent(description) ? (
                                <div
                                    style={{ fontSize: 'calc(13px * var(--theme-font-scale, 1))', color: 'var(--theme-color-text, #374151)', textAlign: 'left', lineHeight: 1.6 }}
                                    dangerouslySetInnerHTML={{ __html: description }}
                                />
                            ) : (
                                <p style={{ fontSize: 'calc(13px * var(--theme-font-scale, 1))', color: 'var(--theme-color-text, #374151)', textAlign: 'left', lineHeight: 1.6 }}>
                                    {description}
                                </p>
                            )}
                        </>
                    )}

                    {merchantName && merchantHref && (
                        <a
                            href={merchantHref}
                            style={{
                                display: 'block',
                                marginTop: '18px',
                                fontSize: 'calc(13px * var(--theme-font-scale, 1))',
                                fontWeight: 600,
                                color: 'var(--theme-color-primary, #f23017)',
                                textDecoration: 'none',
                            }}
                        >
                            Explore more offers from {merchantName} →
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OfferDetailModal;
